import {
	IBooleanSelection,
	IPromoCategory,
	PromoAreaTreeItem,
	DateRangeSelectorItem,
	TDateRangeOperator,
	INumberSelectionBase,
} from '..';
import moment from 'moment/moment';

// export const PROMO_STEP_PROMO_CODE = 1 + PROMO_STEP_DETAIL;

export const PROMO_STEP_DETAIL = 0;
export const PROMO_STEP_FILTER = 1 + PROMO_STEP_DETAIL;
export const PROMO_STEP_FILTER_CUSTOMER = 1 + PROMO_STEP_FILTER;
export const PROMO_STEP_SUMMARY = 1 + PROMO_STEP_FILTER_CUSTOMER;
export const utcTime = 12;

export function inArrearsList(): IBooleanSelection[] {
	return [
		{ id: null, name: 'Not Applicable' },
		{ id: true, name: 'In Arrears' },
		{ id: false, name: 'Up To Date' },
	];
}

export const PRICE_REDUCTION_PROMOTION = 1;
export const PRICE_REDUCTION_DISCOUNT = 2;
export const PRICE_REDUCTION_BOLT_ON = 3;

export enum TPromoCategoryQueryTypeId {
	SingleService = 1,
	MultipleService = 2,
	EligibilityFilter = 3,
	Any = 4,
}

export enum TPromoStatus {
	Active = 1,
	FutureDated = 2,
	Draft = 3,
	Deleted = 4,
	Expired = 5,
}

export function promoStatusAsText(promoStatus: TPromoStatus): string {
	switch (promoStatus) {
		case TPromoStatus.Active:
			return 'Active';
		case TPromoStatus.FutureDated:
			return 'Future Dated';
		case TPromoStatus.Draft:
			return 'Draft';
		case TPromoStatus.Deleted:
			return 'Deleted';
		case TPromoStatus.Expired:
			return 'Expired';
		default:
			return '';
	}
}

export function getPromoFormTitleClass(promoStatus: TPromoStatus): string {
	switch (promoStatus) {
		case TPromoStatus.Draft:
			return 'status-draft';
		case TPromoStatus.Active:
			return 'status-active';
		case TPromoStatus.FutureDated:
			return 'status-future-dated';
		case TPromoStatus.Expired:
		case TPromoStatus.Deleted:
			return 'status-deleted';
		default:
			return '';
	}
}

	export function getPromoStatusClass(status: string): string {
		switch (status.toLowerCase()) {
			case 'active':
				return 'status-active';
			case 'in-active':
				return 'status-future-dated';
			case 'draft':
				return 'status-draft';
			case 'deleted':
				return 'status-deleted';
			default:
				return 'status-default';
		}
	}

export enum TBillingSchemeProductType {
	Recurring = 1,
	DayBundle = 2,
	DayBundleWithAutoRecharge = 3
}

export function getCategoryProcessDescription(
	priceReductionPriceCode: string,
	promoCategoryItem: IPromoCategory,
): string {
	let description: string;
	switch (promoCategoryItem.query_type_id) {
		case TPromoCategoryQueryTypeId.SingleService:
			description = `${priceReductionPriceCode} is applicable to a SINGLE customer.`;
			break;
		case TPromoCategoryQueryTypeId.MultipleService:
			description = `${priceReductionPriceCode} is applicable to MULTIPLE customers.`;
			break;
		case TPromoCategoryQueryTypeId.EligibilityFilter:
			description = `${priceReductionPriceCode} is defined by the ELIGIBILITY FILTER criteria.`;
			break;
		default:
			description = `Unhandled category query type: ${promoCategoryItem.query_type_id}`;
	}

	if (promoCategoryItem.benefit_count_applicable)
		description += ` Period Length is required.`;
	else
		description += ` Period Length cannot be adjusted.`;

	return description;
}

export function findPromoAreaTreeItemById(areaTreeArray: PromoAreaTreeItem[], id: string): PromoAreaTreeItem | null {
	// Search in the current level
	for (const areaTreeItem of areaTreeArray) {
		// Found item?
		if (areaTreeItem.id === id)
			return areaTreeItem;
		// Not found, search in subtree
		if (areaTreeItem.children.length > 0) {
			// Recursive search
			const found = findPromoAreaTreeItemById(areaTreeItem.children, id);
			if (found)
				return found;
		}
		// Not found, continue searching
	}
	return null;
}

export function formatDateYYYYMMDD(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

// Converts a date string in the format YYYY-MM-DD to a Date object in UTC timezone
export function getUTCDate(dateString: string): Date | null {
	if (!dateString)
		return null;
	const delimiter = ['-', '/', '.'].find(d => dateString.includes(d));
	if (!delimiter)
		return null;

	const [year, month, day] = dateString.split(delimiter).map(Number);
	if (!year || !month || !day)
		return null;

	// remember that Javascript's `Date` months are 0-indexed.
	return new Date(Date.UTC(year, month - 1, day, utcTime));
}

export function setDateRangeItem(dateItem: DateRangeSelectorItem) {

	const noEndDateOperators = [
		TDateRangeOperator.Before,
		TDateRangeOperator.BeforeOrOn,
		TDateRangeOperator.On,
		TDateRangeOperator.After,
		TDateRangeOperator.AfterOrOn,
	];

	if (noEndDateOperators.includes(dateItem.date_range_operator))
		dateItem.date_end_value = null;

	dateItem.date_start = dateItem.date_start_value ? formatDateYYYYMMDD(dateItem.date_start_value): null;
	dateItem.date_end = dateItem.date_end_value ? formatDateYYYYMMDD(dateItem.date_end_value): null;
}

export function getDateRangeItemUTCDate(dateItem: DateRangeSelectorItem) {
	dateItem.date_start_value = convertDateStringForGrid(dateItem.date_start);
	dateItem.date_end_value = convertDateStringForGrid(dateItem.date_end);
}

export function convertDateStringForGrid(dateString: string) : Date {
	return moment(dateString)
		.clone()
		.hour(0).minute(0).second(0).millisecond(0)
		.toDate()
}

export const PROMO_OVERVIEW_SUMMARY = 1;
export const PROMO_OVERVIEW_SERVICE = 2;
export const PROMO_OVERVIEW_PURCHASE = 3;

export const promoReportViewSelection: INumberSelectionBase[] = [
  { id: PROMO_OVERVIEW_SUMMARY, 	name: '1. Promotion Overview' },
  { id: PROMO_OVERVIEW_SERVICE, 	name: '2. Overview by Service' },
  { id: PROMO_OVERVIEW_PURCHASE, 	name: '3. Overview by Invoice' },
];

export const boltOnReportViewSelection: INumberSelectionBase[] = [
  { id: PROMO_OVERVIEW_SUMMARY, 	name: '1. Bolt-On Overview' },
  { id: PROMO_OVERVIEW_SERVICE, 	name: '2. Overview by Service' },
  { id: PROMO_OVERVIEW_PURCHASE, 	name: '3. Overview by Invoice' },
];

export const DATE_THIS_MONTH = 0;
export const DATE_LAST_MONTH = 1;
export const DATE_ALL_TIME = 2;
export const DATE_CUSTOM = 3;
export const DATE_FORECAST = 4;
